import useSuitesProjectsLanguage from '../useSuitesProjectsLanguage';
import React from 'react';
import axios from 'axios';
import './projectsTheme.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { TabsContent } from '../ui/tabs';
import { FolderKanban, Plus, Edit, Trash2, Loader2 } from 'lucide-react';

export function ProjectList({
  user,
  projects,
  setEditingProject,
  setProjectForm,
  setShowProjectModal,
  openEditProject,
  deleteProject
}) {
  const { t } = useSuitesProjectsLanguage();
  return (<>
          {/* Projects Tab */}
          <TabsContent value="projects" className="lexa-app-projects mx-auto max-w-7xl space-y-6">
            <div className="space-y-6">
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-3xl font-semibold tracking-tight text-slate-950 flex items-center">
                      <FolderKanban className="h-5 w-5 mr-2 text-purple-600" />
                      {t("Project Management")}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("Organize tests into projects and control team access")}
                    </p>
                  </div>
                  {/* Only admins/owners can create projects */}
                  {(user.role === 'owner' || user.role === 'admin') && (
                    <Button
                      onClick={() => {
                        setEditingProject(null);
                        setProjectForm({ name: '', description: '', icon: '📁', color: '#6366F1' });
                        setShowProjectModal(true);
                      }}
                      className="rounded-full bg-[#6d28d9] hover:bg-[#5b21b6]"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t("New Project")}
                    </Button>
                  )}
                </div>

                {projects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FolderKanban className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p>{t("No projects yet. Create your first project to organize tests.")}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div key={project.id} className="flex flex-wrap items-center justify-between gap-3 p-5 bg-white rounded-2xl border border-slate-200 hover:border-violet-200 transition-colors">
                        <div className="flex items-center space-x-3 min-w-0 max-w-full">
                          <div
                            className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-2xl"
                            style={{ backgroundColor: project.color + '20' }}
                          >
                            {project.icon}
                          </div>
                          <div className="min-w-0 break-words">
                            <div className="font-medium text-slate-950">{project.name}</div>
                            {project.description && (
                              <div className="text-sm text-gray-600">{project.description}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              {project.member_permissions?.length || 0} {t("team members with access")}
                            </div>
                          </div>
                        </div>
                        {/* Only admins/owners can edit/delete projects */}
                        {(user.role === 'owner' || user.role === 'admin') && (
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditProject(project)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              {t("Edit")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                              aria-label={t("Delete project")}
                              onClick={() => deleteProject(project.id, project.name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>


  </>);
}

export function ProjectDialog({
  showProjectModal,
  editingProject,
  projectForm,
  setProjectForm,
  projectLoading,
  teamMembers,
  memberSearchQuery,
  setMemberSearchQuery,
  BACKEND_URL,
  loadProjects,
  setEditingProject,
  showError,
  setShowProjectModal,
  updateProject,
  createProject
}) {
  const { t } = useSuitesProjectsLanguage();
  return (<>
        {/* Project Create/Edit Modal - Global modal for all views */}
        {showProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="lexa-project-dialog bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingProject ? t("Edit Project") : t("Create New Project")}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Project Name *")}
                  </label>
                  <Input
                    placeholder={t("My Project")}
                    value={projectForm.name}
                    onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                    disabled={projectLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Description")}
                  </label>
                  <Textarea
                    placeholder={t("Describe this project...")}
                    value={projectForm.description}
                    onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                    rows={3}
                    disabled={projectLoading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Icon")}
                    </label>
                    <Input
                      placeholder="📁"
                      value={projectForm.icon}
                      onChange={(e) => setProjectForm({ ...projectForm, icon: e.target.value })}
                      disabled={projectLoading}
                      maxLength={2}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Color")}
                    </label>
                    <Input
                      type="color"
                      value={projectForm.color}
                      onChange={(e) => setProjectForm({ ...projectForm, color: e.target.value })}
                      disabled={projectLoading}
                    />
                  </div>
                </div>

                {/* Team Member Permissions (only for edit) */}
                {editingProject && teamMembers.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("Team Access")}
                      </label>
                      <span className="text-xs text-gray-500">
                        {editingProject.member_permissions?.length || 0} {t("of")} {teamMembers.filter(m => m.role === 'member').length} {t("members")}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      {t("Admins can always access all projects")}
                    </div>

                    {/* Search box for filtering members */}
                    {teamMembers.filter(m => m.role === 'member').length > 5 && (
                      <div className="mb-3">
                        <Input
                          placeholder={t("Search members...")}
                          value={memberSearchQuery || ''}
                          onChange={(e) => setMemberSearchQuery(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    )}

                    {/* Member list with improved height and styling */}
                    <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                      {teamMembers
                        .filter(m => m.role === 'member')
                        .filter(m => !memberSearchQuery ||
                          m.email.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                          m.name.toLowerCase().includes(memberSearchQuery.toLowerCase())
                        )
                        .map((member) => {
                          const hasAccess = editingProject.member_permissions?.includes(member.id);
                          return (
                            <label
                              key={member.id}
                              className="flex items-center space-x-3 p-3 hover:bg-white cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                checked={hasAccess}
                                onChange={async (e) => {
                                  try {
                                    await axios.put(
                                      `${BACKEND_URL}/api/projects/${editingProject.id}/permissions`,
                                      {
                                        user_id: member.id,
                                        has_access: e.target.checked
                                      },
                                      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
                                    );
                                    // Reload projects and update editingProject with the latest data
                                    await loadProjects();
                                    const updatedProjectResponse = await axios.get(
                                      `${BACKEND_URL}/api/projects/${editingProject.id}`,
                                      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
                                    );
                                    setEditingProject(updatedProjectResponse.data);
                                  } catch (error) {
                                    console.error('Failed to update permissions:', error);
                                    showError('Failed to update permissions');
                                  }
                                }}
                                className="rounded h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {member.name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {member.email}
                                </div>
                              </div>
                              {hasAccess && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  {t("Access granted")}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      {teamMembers.filter(m => m.role === 'member').filter(m => !memberSearchQuery ||
                        m.email.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                        m.name.toLowerCase().includes(memberSearchQuery.toLowerCase())
                      ).length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                          <p className="text-sm">{t("No members found")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowProjectModal(false);
                    setEditingProject(null);
                    setMemberSearchQuery('');
                  }}
                  disabled={projectLoading}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={editingProject ? updateProject : createProject}
                  disabled={projectLoading || !projectForm.name.trim()}
                  className="rounded-full bg-[#6d28d9] hover:bg-[#5b21b6]"
                >
                  {projectLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingProject ? t("Updating...") : t("Creating...")}
                    </>
                  ) : (
                    <>
                      {editingProject ? t("Update Project") : t("Create Project")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}


  </>);
}
